import { api } from './api'
import * as Sentry from '@sentry/react'
import type { AssistantScope } from './assistantAccess'

export interface UsageSnapshot {
  promptsLastMinute: number
  promptsPerMinuteLimit: number
  promptsToday: number
  promptsPerDayLimit: number
  promptsThisMonth: number
  includedPromptsPerMonth: number
  enabled: boolean
  preferredProvider?: string | null
  ratePlanCode?: string | null
  ratePlanName?: string | null
  /**
   * Who pays for the next prompt (ADR-W22): 'platform' (TOU's Claude, the allowance applies) or
   * 'company' (its own key, no allowance). Absent from older service-core and for platform admins.
   */
  paidBy?: 'platform' | 'company' | null
}

/** Business › Company › AI Assistant (`GET /companies/{id}/assistant`, ADR-W22). */
export interface CompanyAssistantSettings {
  keys: ProviderKey[]
  preferredProvider?: string | null
  usage: UsageSnapshot
  /** What the assistant runs on. Older service-core sends only platform_claude / own_key, or nothing. */
  aiConnection?: 'platform_claude' | 'own_key' | 'own_key_trial' | null
  /** package, merchant (their own choice) or override (set by TOU for this company). */
  connectionSource?: 'package' | 'merchant' | 'override' | null
  /** What the package (or TOU's override) says on its own, before the merchant's choice. */
  packageConnection?: 'platform_claude' | 'own_key' | 'own_key_trial' | null
  merchantSwitch?: boolean
  merchantChoiceLocked?: boolean
  paidBy?: 'platform' | 'company' | null
  hasOwnKey?: boolean
  ownKeyUsageThisMonth?: { prompts: number; inputTokens: number; outputTokens: number } | null
}

/** Platform › AI › Company usage, one company and one month (`GET /admin/assistant/company-usage`). */
export interface CompanyAiUsage {
  companyId: string
  companyName: string
  aiOn: boolean
  connection: 'platform_claude' | 'own_key' | 'own_key_trial'
  connectionSource: 'package' | 'merchant' | 'override'
  merchantSwitch: boolean
  ratePlanName?: string | null
  /** TOU-paid prompts today (current month only). */
  promptsToday: number
  promptsPerDay: number
  platformPrompts: number
  includedPromptsPerMonth: number
  platformInputTokens: number
  platformOutputTokens: number
  /** Estimated, at the model register's prices (same currency as the usage report). */
  platformCost: number
  platformCostComplete: boolean
  companyPrompts: number
  companyInputTokens: number
  companyOutputTokens: number
  /** The company's own on/off switch (off stops every prompt). Absent from the first core build = on. */
  assistantEnabled?: boolean
}

/** All companies for a month, or one company (its Package tab) for this month. */
export async function fetchCompanyAiUsage(month?: string, companyId?: string): Promise<CompanyAiUsage[]> {
  const params: Record<string, string> = {}
  if (month) params.month = month
  if (companyId) params.companyId = companyId
  const res = await api.get<CompanyAiUsage[]>('/admin/assistant/company-usage', {
    params: Object.keys(params).length ? params : undefined,
  })
  return Array.isArray(res.data) ? res.data : []
}

/**
 * The company's assistant on/off switch: core's company quota row, sent back with its current
 * rate plan so only `enabled` changes.
 */
export async function setCompanyAssistantEnabled(companyId: string, enabled: boolean): Promise<void> {
  const quotas = (await api.get<CompanyQuota[]>('/admin/assistant/company-quotas')).data
  const own = (Array.isArray(quotas) ? quotas : []).find((q) => q.companyId === companyId)
  let ratePlanId = own?.ratePlanId
  if (!ratePlanId) {
    const plans = (await api.get<RatePlan[]>('/admin/assistant/rate-plans')).data
    ratePlanId = (Array.isArray(plans) ? plans : []).find((p) => p.isDefault)?.id
  }
  if (!ratePlanId) throw new Error('No allowance to keep for this company.')
  await api.put(`/admin/assistant/company-quotas/${companyId}`, { ratePlanId, enabled })
}

export interface ActionProposal {
  id: string
  toolName: string
  summary: string
  args: Record<string, unknown>
  expiresAt: string
}

export interface DeepLink {
  label: string
  route: string
}

export interface TableBlock {
  title: string
  columns: string[]
  rows: unknown[][]
  reportId?: string | null
}

export interface ChatResponse {
  sessionId: string
  title?: string | null
  reply: string
  proposals: ActionProposal[]
  deepLinks: DeepLink[]
  tables?: TableBlock[]
  usage: UsageSnapshot
  provider: string
  model: string
}

export interface ProviderKey {
  id: string
  scope: string
  companyId?: string | null
  provider: string
  keyLast4: string
  baseUrl?: string | null
  model?: string | null
  active: boolean
  /** When the secret itself last changed. Toggling active or editing the model does not move this. */
  lastRotatedAt?: string | null
  /** Display name of whoever rotated it. */
  lastRotatedBy?: string | null
}

/** Claude Workload Identity Federation — GET/PUT /admin/assistant/wif, POST /admin/assistant/wif/test. */
export const WIF_PATH = '/admin/assistant/wif'

/** auto = stored key, then workload identity, then env key · wif = workload identity only · key = keys only */
export type ClaudeCredentialMode = 'auto' | 'wif' | 'key'

/** IDs only — none of these are secrets. */
export interface WifSettings {
  enabled: boolean
  organizationId: string | null
  workspaceId: string | null
  serviceAccountId: string | null
  federationRuleId: string | null
  audience: string
  claudeCredentialMode: ClaudeCredentialMode
  modifiedAt?: string | null
  modifiedBy?: string | null
}

/** What the Claude Console issuer and rule need, read from the cluster by service-core. */
export interface WifClusterFacts {
  namespace: string | null
  serviceAccount: string | null
  /** Exact rule subject, system:serviceaccount:<ns>:service-core */
  subject: string | null
  /** Who the pod runs as right now. */
  podSubject: string | null
  issuer: string | null
  signingKeyId: string | null
  /** The JWKS `keys` array — the Console issuer's inline keys. */
  keys: unknown[] | null
  error: string | null
}

export interface WifExchangeStatus {
  at: string | null
  ok: boolean | null
  error: string | null
  expiresAt: string | null
  scope: string | null
  lastSuccessAt: string | null
  lastSuccessKid: string | null
}

export interface WifTestResult {
  ok: boolean
  failedStep: 'exchange' | 'ping' | null
  error: string | null
  at: string | null
  expiresAt: string | null
  scope: string | null
  keyId: string | null
  model: string | null
}

export interface WifView {
  settings: WifSettings
  cluster: WifClusterFacts | null
  status: WifExchangeStatus | null
  /** The cluster signs with a different key than the last exchange that worked. */
  keyChanged: boolean
  /** Set on a save that ran the live test. */
  test: WifTestResult | null
}

export interface RatePlan {
  id: string
  code: string
  displayName: string
  promptsPerMinute: number
  promptsPerDay: number
  includedPromptsPerMonth: number
  overageRateEur: number
  hardStopOnDaily: boolean
  hardStopOnMonthly: boolean
  isDefault: boolean
}

export interface CompanyQuota {
  companyId: string
  companyName: string
  ratePlanId: string
  ratePlanCode: string
  promptsPerMinute?: number | null
  promptsPerDay?: number | null
  includedPromptsPerMonth?: number | null
  overageRateEur?: number | null
  preferredProvider?: string | null
  enabled: boolean
  usage: UsageSnapshot
}

export type AssistantUiContext = {
  path: string
  viewLabel: string
  companyId?: string | null
  companyName?: string | null
  companyRole?: string | null
  activePersona?: string | null
  activeRole?: string | null
  isPlatformAdmin?: boolean
  isOwner?: boolean
  permissions?: string[]
  impersonation?: {
    targetUserId: string
    companyId?: string | null
    targetName?: string | null
  } | null
  selection?: Record<string, string>
  /** Client surface for Confirm-all / deep-link awareness. */
  client?: 'backoffice' | 'worker'
  uiActions?: {
    confirmAll?: boolean
    rejectAll?: boolean
  }
}

/**
 * `scope`: 'platform' (Platform AI, platform admins in the Admin persona) or 'company' (the
 * company's AI, which follows its package). Core treats an absent scope as 'company'.
 */
export async function assistantChat(body: {
  sessionId?: string | null
  message: string
  provider?: string | null
  imageUrls?: string[] | null
  uiContext?: AssistantUiContext | null
  scope?: AssistantScope
}): Promise<ChatResponse> {
  try {
    // Image attach + Claude/Gemini tool rounds can exceed 90s when upstreams retry.
    const res = await api.post<ChatResponse>('/assistant/chat', body, { timeout: 150_000 })
    return res.data
  } catch (err: any) {
    try {
      Sentry.captureException(err, {
        extra: {
          sessionId: body.sessionId,
          provider: body.provider,
          axiosError: {
            status: err?.response?.status,
            statusText: err?.response?.statusText,
            message: err?.message,
            responseData: err?.response?.data,
            url: err?.config ? `${err.config.baseURL || ''}${err.config.url || ''}` : null
          }
        }
      })
    } catch (sentryErr) {
      console.error('Failed to log to Sentry:', sentryErr)
    }
    throw err
  }
}

const MAX_ASSISTANT_IMAGE_BYTES = 5 * 1024 * 1024

/** Upload an image for assistant attach; returns `/api/media/files/...` or data URI. */
export async function assistantUploadImage(file: File): Promise<string> {
  if (file.size > MAX_ASSISTANT_IMAGE_BYTES) {
    throw new Error('Image exceeds the 5 MB upload limit')
  }
  const formData = new FormData()
  formData.append('file', file)
  const res = await api.post<{ url: string }>('/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  if (!res.data?.url) {
    throw new Error('Upload returned no URL')
  }
  return res.data.url
}

export type ConfirmResponse = {
  proposalId: string
  status: string
  resultSummary?: string | null
  result?: unknown
  deepLinks?: DeepLink[] | null
}

/**
 * `scope` is informational: core checks a confirm against the scope the proposal's chat was
 * started with, not against this field.
 */
export async function assistantConfirm(
  proposalId: string,
  confirm: boolean,
  scope?: AssistantScope,
): Promise<ConfirmResponse> {
  // Bulk schedule publish can exceed the default 30s axios timeout (ingress allows 180s).
  const res = await api.post<ConfirmResponse>(
    '/assistant/confirm',
    scope ? { proposalId, confirm, scope } : { proposalId, confirm },
    { timeout: 180_000 },
  )
  return res.data
}

export type ConfirmAllResponse = {
  results: ConfirmResponse[]
  confirmed: number
  rejected: number
  failed: number
}

/** Confirm or reject many pending proposals in one request (server processes sequentially). */
export async function assistantConfirmAll(
  proposalIds: string[],
  confirm: boolean,
  scope?: AssistantScope,
): Promise<ConfirmAllResponse> {
  // Many shift creates / month publishes can run long; ingress allows 180s.
  const res = await api.post<ConfirmAllResponse>(
    '/assistant/confirm-all',
    scope ? { proposalIds, confirm, scope } : { proposalIds, confirm },
    { timeout: 180_000 },
  )
  return res.data
}

export async function assistantUsage(): Promise<UsageSnapshot> {
  const res = await api.get<UsageSnapshot>('/assistant/usage')
  return res.data
}

export interface AssistantSession {
  id: string
  title: string
  provider?: string | null
  createdAt?: string | null
  modifiedAt?: string | null
  pinned?: boolean
}

export interface SessionMessage {
  id: string
  role: 'user' | 'assistant' | string
  content: string
  createdAt?: string | null
}

export interface SessionMessagesResponse {
  sessionId: string
  title: string
  pinned?: boolean
  messages: SessionMessage[]
  pendingProposals: ActionProposal[]
}

export async function assistantListSessions(): Promise<AssistantSession[]> {
  const res = await api.get<AssistantSession[]>('/assistant/sessions')
  return Array.isArray(res.data) ? res.data : []
}

export async function assistantSessionMessages(sessionId: string): Promise<SessionMessagesResponse> {
  const res = await api.get<SessionMessagesResponse>(`/assistant/sessions/${sessionId}/messages`)
  return res.data
}

export async function assistantUpdateSession(
  sessionId: string,
  body: { title?: string; pinned?: boolean },
): Promise<AssistantSession> {
  const res = await api.patch<AssistantSession>(`/assistant/sessions/${sessionId}`, body)
  return res.data
}

export async function assistantRenameSession(sessionId: string, title: string): Promise<AssistantSession> {
  return assistantUpdateSession(sessionId, { title })
}

export async function assistantPinSession(sessionId: string, pinned: boolean): Promise<AssistantSession> {
  return assistantUpdateSession(sessionId, { pinned })
}

export async function assistantDeleteSession(sessionId: string): Promise<void> {
  await api.delete(`/assistant/sessions/${sessionId}`)
}

/** Attach pending proposals to the last assistant message for Confirm UI. */
export function mapSessionMessagesToChat(
  messages: SessionMessage[],
  pendingProposals: ActionProposal[],
): Array<{
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  proposals?: ActionProposal[]
}> {
  const out = messages.map((m) => ({
    id: m.id,
    role: (m.role === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
    content: m.content ?? '',
  }))
  if (pendingProposals.length > 0 && out.length > 0) {
    for (let i = out.length - 1; i >= 0; i--) {
      if (out[i].role === 'assistant') {
        return out.map((msg, idx) =>
          idx === i ? { ...msg, proposals: pendingProposals } : msg,
        )
      }
    }
  }
  return out
}
